import * as THREE from 'three';

export interface PeriodLowerGarmentOptions {
  tunic: number;
  trim: number;
  longRobe: boolean;
}

let weave: THREE.DataTexture | undefined;

function cloth(color: number): THREE.MeshStandardMaterial {
  if (!weave) {
    const size = 64;
    const data = new Uint8Array(size * size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        data[y * size + x] = Math.round(
          128
          + Math.sin(x * Math.PI * 0.5) * 16
          + Math.sin(y * Math.PI * 0.5) * 12
          + Math.sin(x * 1.71 + y * 2.37) * 4,
        );
      }
    }
    weave = new THREE.DataTexture(data, size, size, THREE.RedFormat);
    weave.wrapS = THREE.RepeatWrapping;
    weave.wrapT = THREE.RepeatWrapping;
    weave.repeat.set(6, 10);
    weave.needsUpdate = true;
  }
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.96,
    metalness: 0,
    bumpMap: weave,
    bumpScale: 0.015,
    side: THREE.DoubleSide,
  });
}

/**
 * A split, layered first-century lower tunic.  Front/back/side drapes leave a
 * readable human leg silhouette instead of forming the unmistakable cone or
 * cylinder produced by a single primitive robe mesh.
 */
export function createPeriodLowerGarment(options: PeriodLowerGarmentOptions): THREE.Group {
  const root = new THREE.Group();
  root.name = 'period-lower-garment';
  const tunic = cloth(options.tunic);
  const height = options.longRobe ? 1.16 : 0.64;
  const centerY = options.longRobe ? 0.64 : 0.94;
  const topWidth = options.longRobe ? 0.64 : 0.68;
  const bottomWidth = options.longRobe ? 0.92 : 0.82;

  const panel = (front: boolean, widthScale = 1): THREE.Mesh => {
    const geometry = new THREE.PlaneGeometry(1, height, 10, 14);
    const position = geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let index = 0; index < position.count; index += 1) {
      const originalX = position.getX(index);
      const originalY = position.getY(index);
      const v = originalY / height + 0.5;
      const halfWidth = THREE.MathUtils.lerp(bottomWidth, topWidth, v) * 0.5 * widthScale;
      const normalizedX = originalX / 0.5;
      const fold = Math.sin((normalizedX + 1) * Math.PI * 3.5 + (front ? 0.4 : 1.1));
      const hemBreak = Math.sin((normalizedX + 1.2) * 4.7) * (1 - v) * 0.025;
      const depth = fold * (0.028 + (1 - v) * 0.018) + Math.abs(normalizedX) * 0.022;
      position.setXYZ(index, normalizedX * halfWidth, originalY + hemBreak, depth);
    }
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, tunic);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(0, centerY, front ? 0.235 : -0.19);
    if (!front) mesh.rotation.y = Math.PI;
    return mesh;
  };

  root.add(panel(true), panel(false));

  // Narrow overlapping side falls preserve volume in profile while leaving a
  // subtle split for walking and kneeling poses.
  for (const side of [-1, 1]) {
    const fall = panel(true, options.longRobe ? 0.52 : 0.3);
    fall.position.set(side * (options.longRobe ? 0.29 : 0.34), centerY, 0.01);
    fall.rotation.y = side * Math.PI * 0.46;
    root.add(fall);
  }

  return root;
}
